import React from "react";
import {
  Globe,
  Workflow,
  MessageSquare,
  LineChart,
  ShieldCheck,
  Bot,
  ArrowUpRight,
} from "lucide-react";

const ENGINES = [
  {
    id: "websites",
    title: "AI Websites",
    body: "Conversion-engineered business sites with AI search, copy and lead capture built in.",
    points: ["Modern stack", "AI copy & SEO", "Lead routing"],
    Icon: Globe,
  },
  {
    id: "automation",
    title: "AI Automation",
    body: "Replace manual back-office work with intelligent workflows wired into your tools.",
    points: ["Lead qualification", "Appointments", "CRM & email"],
    Icon: Workflow,
  },
  {
    id: "chatbots",
    title: "AI Chatbots",
    body: "Site, support and WhatsApp chatbots that handle FAQs, bookings and handoffs.",
    points: ["Web widget", "WhatsApp", "Deflection"],
    Icon: MessageSquare,
  },
  {
    id: "analytics",
    title: "AI Data Analytics",
    body: "KPI dashboards, forecasts and natural-language exploration on top of your data.",
    points: ["Dashboards", "Forecasting", "Exec reporting"],
    Icon: LineChart,
  },
  {
    id: "compliance",
    title: "AI Compliance",
    body: "Workflow automation, onboarding support, document validation and reporting solutions for regulated environments.",
    points: ["KYC workflows", "Document review", "Reporting", "Operational controls"],
    Icon: ShieldCheck,
  },
  {
    id: "agents",
    title: "AI Agents",
    body: "Production agents for reception, sales, support, HR and operations — trained on your processes.",
    points: ["Receptionist", "Sales & support", "Operations"],
    Icon: Bot,
  },
];

export const ServiceEngines = () => {
  return (
    <section
      id="services"
      data-testid="engines-section"
      className="relative py-24 lg:py-32 bg-page"
    >
      <div className="mx-auto max-w-7xl px-5 lg:px-10">
        <div className="text-center">
          <span className="chip">
            <span className="chip-dot" />
            <span className="font-mono text-[12px] text-ink-200">
              Six AI Engines
            </span>
          </span>
          <h2
            className="mt-8 headline mx-auto max-w-4xl"
            style={{ fontSize: "clamp(1.85rem, 4.2vw, 3.4rem)" }}
          >
            Independent engines. <em>One platform.</em>
          </h2>
          <p className="mt-8 max-w-2xl mx-auto text-ink-200/75 text-[16.5px] lg:text-[17px] leading-[1.75] font-light">
            Each engine is production-grade and composable — pick one, or
            combine them into a full AI stack for your business.
          </p>
        </div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {ENGINES.map((e, i) => (
            <a
              key={e.id}
              href="#contact"
              data-testid={`engine-card-${e.id}`}
              className="card-surface p-7 lg:p-8 group block"
            >
              <div className="flex items-start justify-between">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#EFF4FF] to-[#DCE6FE] border border-brand-glow/30 flex items-center justify-center">
                  <e.Icon size={20} className="text-brand" strokeWidth={1.6} />
                </div>
                <span className="font-mono text-[11px] tracking-tight text-ink-200/50">
                  {String(i + 1).padStart(2, "0")} / 06
                </span>
              </div>

              <h3 className="mt-7 text-[24px] font-medium tracking-tight text-ink-50 leading-tight">
                {e.title}
              </h3>
              <p className="mt-3 text-[15px] leading-[1.65] text-ink-200/75 font-light">
                {e.body}
              </p>

              <ul className="mt-6 space-y-2">
                {e.points.map((p) => (
                  <li
                    key={p}
                    className="flex items-center gap-2.5 text-[13px] text-ink-200/80"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-brand-light" />
                    {p}
                  </li>
                ))}
              </ul>

              <div className="mt-8 inline-flex items-center gap-1.5 text-[13px] font-medium text-ink-100 group-hover:gap-2.5 transition-all">
                Discuss
                <ArrowUpRight size={14} />
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServiceEngines;
